/*
 * This is the only way in which it is possible to suppress this lint rule.
 * When referencing the Nav4NotificationsData component directly we introduce a cyclic dependency
 * between @atlassian/jira-atlassian-navigation and @atlassian/jira-atlassian-navigation-nav4
 * (and cyclic dependency errors be suppressed to pass the build).
 */
/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
/** @jsx jsx */
import React, { useMemo, useCallback, useEffect } from 'react';
import { jsx } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import {
	TopNavStart,
	TopNavMiddle,
	TopNavEnd,
	SideNavToggleButton,
	type SideNavVisibilityChangeAnalyticsAttributes,
} from '@atlaskit/navigation-system';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items';
import { useIntl } from '@atlassian/jira-intl';
import { topNav4UIStateResource } from '@atlassian/jira-navigation-apps-resources/src/services/top-nav/index.tsx';
import { V4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TOP } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { GLOBAL } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getSideNavToggleButtonAnalyticEventPayload } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { getNav4Rollout } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-nav4-rollout/index.tsx';
import { IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { AsyncGPAwarenessPill } from '@atlassian/jira-jsm-grace-period-banner/src/ui/gp-awareness-pill/async.tsx';
import { GpAwarenessPill } from '@atlassian/jira-jsm-grace-period-banner/src/ui/gp-awareness-pill/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { useSetCustomThemeData } from '@atlassian/jira-navigation-apps-common/src/controllers/custom-theme-context/index.tsx';
import type { AtlassianNavigationNav4Query } from './__generated__/AtlassianNavigationNav4Query.graphql';
import { messages } from './messages.tsx';
import { Nav4Profile } from './profile/Nav4Profile.tsx';
import type { AtlaskitAtlassianNavigationNav4Props } from './types.tsx';
import { useSiteTitle } from './site-title/index.tsx';
import { ProductHomeRelayWithErrorBoundary as ProductHomeRelay } from './prodcut-home-relay/ProductHomeRelay.tsx';

export function AtlassianNavigationNav4(
	props: AtlaskitAtlassianNavigationNav4Props,
): JSX.Element | null {
	const { queryReference } = useRelayResource(topNav4UIStateResource);

	// This intermediate step is to avoid using a null queryReference.
	// Maybe this should be a skeleton? Or is that taken care of by the page-container-v2?
	return queryReference && <TopNavContent queryReference={queryReference} {...props} />;
}

type TopNavContentProps = AtlaskitAtlassianNavigationNav4Props & {
	queryReference: PreloadedQuery<AtlassianNavigationNav4Query>;
};

export function TopNavContent({
	hideSideNavToggleButton,
	queryReference,
	renderCreate: Create,
	renderHelp: Help,
	renderAppSwitcher: AppSwitcher,
	renderNotifications: Notifications,
	renderNav4Notifications: Nav4NotificationsData,
	renderConversationAssistant: ConversationAssistant,
	renderSettings: Settings,
	renderSearch: Search,
	renderEditionAwareness: EditionAwareness,
	renderProductHome: ProductHome,
}: TopNavContentProps): JSX.Element {
	const data = usePreloadedQuery(
		graphql`
			query AtlassianNavigationNav4Query(
				$cloudId: ID!
				$isAnonymous: Boolean!
				$atlassianAccountId: ID!
				$isNavStateQuerySkipped: Boolean!
				$issueKey: String!
				$shouldCheckProjectTypeForIssue: Boolean!
				$projectKey: String!
				$shouldCheckProjectType: Boolean!
			) {
				jira @skip(if: $isNavStateQuerySkipped) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
					}
				}
				...Nav4Profile @skip(if: $isAnonymous)
				...Nav4NotificationsData @skip(if: $isAnonymous)
				...ProductHomeRelay
			}
		`,
		queryReference,
	);

	const isAnonymous = useIsAnonymous();
	const { formatMessage } = useIntl();

	const siteTitleData = useSiteTitle();
	const siteTitle = (siteTitleData.shouldShow && siteTitleData.value) || undefined;

	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	if (fg('jfp-magma-fix-custom-theme-data-hydration-error')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			setCustomThemeData({
				hasCustomSiteTitle: !!siteTitle,
				customSiteTitleLength: siteTitle?.length || undefined,
			});
		}, [setCustomThemeData, siteTitle]);
	} else {
		setCustomThemeData({
			hasCustomSiteTitle: !!siteTitle,
			customSiteTitleLength: siteTitle?.length || undefined,
		});
	}

	const attributes = useMemo(
		() => ({
			navigationLayer: GLOBAL,
			navigationContainer: TOP,
			navState: V4,
			stage: getNav4Rollout().stage,
		}),
		[],
	);

	const onClickSideNavToggleButton = useCallback(
		(
			_: React.MouseEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
			_attributes: SideNavVisibilityChangeAnalyticsAttributes | undefined,
		) => {
			const payload = getSideNavToggleButtonAnalyticEventPayload(
				_attributes?.isSideNavVisible,
				getNav4Rollout().stage,
			);

			fireUIAnalytics(analyticsEvent, payload);
		},
		[],
	);

	const renderNotificationsForNavVersion = useCallback(() => {
		if (Notifications !== undefined) {
			if (Nav4NotificationsData) {
				return <Nav4NotificationsData queryReference={data} />;
			}

			return <Notifications />;
		}

		return null;
	}, [Nav4NotificationsData, Notifications, data]);

	const { isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();
	const defaultCollapsed = useMemo(() => {
		if (fg('blu-8363-archive-legacy-resource-navigationuistate')) {
			return isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT;
		}
		return (
			data?.jira?.navigationUIState?.isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT
		);
	}, [data?.jira?.navigationUIState?.isLeftSidebarCollapsed, isLeftSidebarCollapsed]);

	const sideNavToggleButton = hideSideNavToggleButton ? null : (
		<SideNavToggleButton
			defaultCollapsed={fg('navx-full-height-sidebar') ? undefined : defaultCollapsed}
			onClick={onClickSideNavToggleButton}
			collapseLabel={
				fg('navx-full-height-sidebar')
					? formatMessage(messages.sideNavToggleButtonCollapseLabel)
					: formatMessage(messages.sideNavToggleButtonCollapseLabelWithShortcut)
			}
			expandLabel={
				fg('navx-full-height-sidebar')
					? formatMessage(messages.sideNavToggleButtonExpandLabel)
					: formatMessage(messages.sideNavToggleButtonExpandLabelWithShortcut)
			}
		/>
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<TopNavStart
				sideNavToggleButton={
					fg('platform_dst_nav4_side_nav_toggle_button_slot') ? sideNavToggleButton : undefined
				}
			>
				{fg('platform_dst_nav4_side_nav_toggle_button_slot') ? undefined : sideNavToggleButton}
				{AppSwitcher && <AppSwitcher />}
				{ProductHome &&
					(fg('team25-eu-jira-logo-updates-csm-jsm') ? (
						<ProductHomeRelay renderProductHome={ProductHome} queryReference={data} />
					) : (
						<ProductHome />
					))}
				{siteTitle && <CustomTitle>{siteTitle}</CustomTitle>}
			</TopNavStart>
			<TopNavMiddle>
				{Search && <Search />}
				{Create && <Create />}
			</TopNavMiddle>
			<TopNavEnd>
				{fg('jsm_gp_awareness_pill') &&
					(fg('jsm_gp_awareness_pill_browser_only') ? (
						<AsyncGPAwarenessPill analyticsOrigin="navBar" />
					) : (
						<GpAwarenessPill analyticsOrigin="navBar" />
					))}
				{EditionAwareness && <EditionAwareness />}
				{!isAnonymous && ConversationAssistant && <ConversationAssistant />}
				{!isAnonymous && renderNotificationsForNavVersion()}
				{Help && <Help />}
				{Settings && <Settings />}
				<Nav4Profile queryReference={data} />
			</TopNavEnd>
		</ContextualAnalyticsData>
	);
}
